import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon } from '../Icon';

/** Composants de formulaire du design system (`Docs/design/Design system/components/forms`). */

export function FieldError({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <span id={id} className="lh-ferr" role="alert">
      <Icon name="alertcircle" size={16} />
      {children}
    </span>
  );
}

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  help?: string;
  /** Message d'erreur affiché sous le champ (bordure danger). */
  error?: string;
  /** Bordure danger sans message (le message est affiché ailleurs). */
  invalid?: boolean;
}

export function TextField({ label, help, error, invalid, ...rest }: FieldProps) {
  const id = useId();
  const bad = Boolean(error) || invalid === true;
  return (
    <div className={'lh-field' + (bad ? ' lh-field-err' : '')}>
      <label className="lh-label" htmlFor={id}>{label}</label>
      <input id={id} className="lh-input" aria-invalid={bad ? true : undefined} aria-describedby={error ? id + '-err' : undefined} {...rest} />
      {error ? <FieldError id={id + '-err'}>{error}</FieldError> : help ? <span className="lh-help">{help}</span> : null}
    </div>
  );
}

/** Champ mot de passe avec bouton afficher / masquer. */
export function PasswordField({ label, help, error, invalid, ...rest }: FieldProps) {
  const id = useId();
  const [shown, setShown] = useState(false);
  const bad = Boolean(error) || invalid === true;
  return (
    <div className={'lh-field' + (bad ? ' lh-field-err' : '')}>
      <label className="lh-label" htmlFor={id}>{label}</label>
      <div className="lh-pw">
        <input id={id} className="lh-input" type={shown ? 'text' : 'password'} aria-invalid={bad ? true : undefined} aria-describedby={error ? id + '-err' : undefined} {...rest} />
        <button type="button" className="lh-pw-btn" aria-pressed={shown} aria-label={shown ? 'Masquer le mot de passe' : 'Afficher le mot de passe'} onClick={() => setShown(!shown)}>
          <Icon name={shown ? 'eyeoff' : 'eye'} size={20} />
        </button>
      </div>
      {error ? <FieldError id={id + '-err'}>{error}</FieldError> : help ? <span className="lh-help">{help}</span> : null}
    </div>
  );
}

const STRENGTH_LABELS = ['', 'Faible', 'Moyen', 'Bon', 'Fort'];

/** Score simple de 0 à 4 : longueur (8, 12), casse mêlée, chiffre + symbole. */
export function passwordScore(password: string): number {
  let n = 0;
  if (password.length >= 8) n++;
  if (password.length >= 12) n++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) n++;
  if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) n++;
  return password ? Math.max(1, n) : 0;
}

/** Jauge de force à 4 segments : faible (danger), moyen (attention), bon (info), fort (succès). */
export function StrengthMeter({ score }: { score: number }) {
  const s = Math.max(0, Math.min(4, score));
  return (
    <div className="lh-str" data-s={s} role="img" aria-label={'Force du mot de passe : ' + (STRENGTH_LABELS[s] || 'non évaluée')}>
      <div className="lh-str-bar"><i /><i /><i /><i /></div>
      <span className="lh-str-l">{STRENGTH_LABELS[s] || ' '}</span>
    </div>
  );
}
